import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';

import { GetSessionError } from '~/backend/connector-api/connector-components';
import ImageAndStructureSetDisplay from '~/components/InputDataPreview/ImageAndStructureSetDisplay';
import UnapprovedInputsWarning from '~/components/InputDataPreview/UnapprovedInputsWarning';
import { Modality } from '~/domain/dicom/modality';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import RegistrationModelSelection from './ContourPropagationModelSelection';
import { RegistrationTargetImage } from './registrationTargetImage';
import TargetImageIdentity from './TargetImageIdentity';
import DosePropagationSelect from './DosePropagationSelect';
import { outlinedPickerSx } from '~/theme/outlinedPickerSx';
import { PropagationDoseChoice } from './usePropagationDoseChoice';

type RegistrationModalProps = {
    targetImages: RegistrationTargetImage[];
    selectedTargetImage: RegistrationTargetImage | undefined;
    setSelectedTargetImage: (
        image: RegistrationTargetImage | undefined
    ) => void;
    availableModels: PredictionModel[];
    sourceModality?: Modality;
    onSubmit: (
        selectedModel: PredictionModel,
        selectedStructures: string[],
        selectedCustomization?: string
    ) => void;
    isLoadingModels: boolean;
    isLoadingSession?: boolean;
    isSubmitting?: boolean;
    showPseudonymization: boolean;
    // Only a backend that carries a dose offers the choice.
    doseChoice?: PropagationDoseChoice;
    error: Error | GetSessionError | null;
};

export default function RegistrationModal({
    availableModels,
    sourceModality,
    onSubmit,
    targetImages,
    error,
    isLoadingModels,
    isLoadingSession,
    isSubmitting,
    selectedTargetImage,
    setSelectedTargetImage,
    showPseudonymization,
    doseChoice,
}: RegistrationModalProps) {
    const primaryImage = useSelectedPrimaryImage();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={2}
            height="100%"
            minHeight={0}
        >
            <Box flexShrink={0}>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    <FormattedMessage id="runRegistration" />
                </Typography>
                <Typography variant="body2">
                    <FormattedMessage id="runRegistrationDescription" />
                </Typography>
            </Box>
            <Box
                display="flex"
                flexDirection="column"
                gap={2}
                flex={1}
                minHeight={0}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    overflow="auto"
                    minHeight={0}
                    pt={2}
                >
                    <ImageAndStructureSetDisplay
                        image={primaryImage}
                        structureSet={currentStructureSet}
                        isLoading={isLoadingSession}
                        error={error}
                    />
                    <UnapprovedInputsWarning />
                    <FormControl
                        fullWidth
                        sx={outlinedPickerSx(
                            selectedTargetImage ? 'primary' : 'warning'
                        )}
                    >
                        <InputLabel
                            shrink
                            id="target-image-select-label"
                            size="small"
                        >
                            Target Image
                        </InputLabel>

                        <Select
                            notched
                            data-testid="adaptTargetImageSelect"
                            labelId="target-image-select-label"
                            id="target-image-select"
                            label={'Target Image'}
                            value={selectedTargetImage?.seriesId ?? ''}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) {
                                    return (
                                        <Typography
                                            fontSize={14}
                                            color="text.secondary"
                                        >
                                            Select an image
                                        </Typography>
                                    );
                                }
                                const selectedImage = targetImages?.find(
                                    (img) => img.seriesId === selected
                                );
                                return (
                                    selectedImage && (
                                        <TargetImageIdentity
                                            image={selectedImage}
                                        />
                                    )
                                );
                            }}
                            onChange={(event) => {
                                const selected = targetImages?.find(
                                    (img) => img.seriesId === event.target.value
                                );
                                setSelectedTargetImage(selected ?? undefined);
                            }}
                            size="small"
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        mt: 0.5,
                                    },
                                },
                            }}
                        >
                            {targetImages?.map((image) => (
                                <MenuItem
                                    key={image.seriesId}
                                    value={image.seriesId}
                                >
                                    <TargetImageIdentity image={image} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {doseChoice && (
                        <DosePropagationSelect choice={doseChoice} />
                    )}
                </Box>
                <RegistrationModelSelection
                    availableModels={availableModels}
                    modality={sourceModality}
                    onSubmit={onSubmit}
                    isLoadingModels={isLoadingModels}
                    disabled={isLoadingModels || !selectedTargetImage}
                    isSubmitting={isSubmitting}
                    showPseudonymization={showPseudonymization}
                />
            </Box>
        </Box>
    );
}
